import { type ReactNode, useMemo } from 'react';
import type {
    BreadcrumbData,
    BreadcrumbsRoute,
} from 'use-react-router-breadcrumbs';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useHierarchyBreadcrumbs } from '@hooks/breadcrumbs';
import routes from '@components/navigation/routes';
import s from './content-controller.module.scss';

interface Props {
    title: ReactNode;
    className?: string;
    children?: ReactNode;
}

interface BreadcrumbItem {
    pathname: string;
    label: ReactNode;
    isLast: boolean;
}

type RouteBreadcrumb = BreadcrumbData;

const mapRouteToBreadcrumbItem = (
    breadcrumbs: RouteBreadcrumb[],
    markLastAsLast = true,
): BreadcrumbItem[] =>
    breadcrumbs.map(({ match, breadcrumb }, index) => ({
        pathname: match.pathname,
        label: breadcrumb,
        isLast: markLastAsLast && index === breadcrumbs.length - 1,
    }));

export default function ContentController({
    className,
    title,
    children,
}: Props) {
    const location = useLocation();
    const routeBreadcrumbs = useBreadcrumbs(routes as BreadcrumbsRoute[]);
    const hierarchyBreadcrumbs = useHierarchyBreadcrumbs();
    const headerClasses = clsx(s.header, className);

    const finalBreadcrumbs = useMemo((): BreadcrumbItem[] => {
        if (!hierarchyBreadcrumbs) {
            return mapRouteToBreadcrumbItem(routeBreadcrumbs);
        }

        const isSystemRoute = location.pathname.includes(
            '/systems-and-assets/',
        );
        const isZoneRoute = location.pathname.includes(
            '/security-zones-assets/',
        );

        if (!isSystemRoute && !isZoneRoute) {
            return mapRouteToBreadcrumbItem(routeBreadcrumbs);
        }

        const spliceIndex = routeBreadcrumbs.findIndex(({ match }) => {
            if (isSystemRoute) {
                return match.pathname.endsWith('/systems-and-assets');
            }
            return match.pathname.endsWith('/security-zones-assets');
        });

        if (spliceIndex === -1) {
            return mapRouteToBreadcrumbItem(routeBreadcrumbs);
        }

        const baseBreadcrumbs = mapRouteToBreadcrumbItem(
            routeBreadcrumbs.slice(0, spliceIndex + 1),
            false,
        );

        const hierarchyItems: BreadcrumbItem[] = hierarchyBreadcrumbs.map(
            (item) => ({
                pathname: item.pathname,
                label: item.name,
                isLast: item.isLast,
            }),
        );

        return [...baseBreadcrumbs, ...hierarchyItems];
    }, [routeBreadcrumbs, hierarchyBreadcrumbs, location.pathname]);

    return (
        <header className={headerClasses}>
            <nav
                aria-label="Breadcrumb"
                className="flex gap-5 justify-content-start align-items-center"
            >
                {finalBreadcrumbs.map(({ pathname, label, isLast }) => {
                    const titleText =
                        typeof label === 'string' ? label : undefined;
                    return (
                        <NavLink
                            className={s.navLink}
                            key={pathname}
                            to={pathname}
                            end
                            aria-current={isLast ? 'page' : undefined}
                            title={titleText}
                        >
                            <span className={s.navLinkText}>{label}</span>
                        </NavLink>
                    );
                })}
            </nav>
            <div className="flex align-items-center justify-content-between">
                {typeof title === 'string' ? <h1>{title}</h1> : title}
                {children}
            </div>
        </header>
    );
}
